/* ==========================================================================
   st-waveform-anim.js — BODY / BUILDER waveform animation (st-bb-waveform)
   --------------------------------------------------------------------------
   Loaded ONLY by sections/st-bb-waveform.liquid, deferred. Deliberately kept
   out of streaky.js: that ships on every page and this is canvas maths used on
   one product page.

   The waveform build, the process() curve, the CYCLE/envelope timing, the knob
   directions (BODY clockwise, BUILDER anticlockwise) and the type treatment are
   ported unchanged from the signed-off prototype. What is new here is the
   plumbing the prototype did not have: retina backing store, pause off screen,
   prefers-reduced-motion, and a portrait layout under ~700px.
   ========================================================================== */
(function () {
  'use strict';

  var CYCLE = 11.4;
  var LOOPS = 2;      // demo cycles before it hands over
  var HINT_FADE = 0.8;
  var DRAG_FULL = 240;  // CSS px of vertical drag from zero to full
  /* The knob is drawn in code, permanently. There is no artwork path. */

  var KEYS = ['body', 'builder'];   // index order matches L.knobs
  /* Drag sign per knob: BODY rises when you drag up, BUILDER rises when you drag
     DOWN, matching the anticlockwise direction its artwork turns. */
  var DRAG_SIGN = { body: 1, builder: -1 };

  /* ---- knob artwork -------------------------------------------------------
     One 2048x2048 sheet, 8x8, 64 frames of 256px. Frame 0 is fully
     anticlockwise, 63 fully clockwise, so BODY reads it forwards and BUILDER
     reads it backwards and turns anticlockwise for free. The knob occupies
     94.5% of each frame (measured from the alpha bounding box), so a frame is
     drawn slightly larger than the knob diameter to land at the right size.
     Loaded once for the whole page; until it decodes the drawn vector knob is
     used, so a slow connection never shows a gap. */
  var KNOB_SHEET  = 'https://cdn.shopify.com/s/files/1/0827/7609/7060/files/bb2-knob-sheet.webp?v=1787670659';
  var KNOB_FRAMES = 64;
  var KNOB_COLS   = 8;
  var KNOB_FILL   = 0.945;

  var sheetImg = null, sheetReady = false, sheetWaiters = [];
  (function preloadSheet() {
    if (!KNOB_SHEET) return;
    var im = new Image();
    im.decoding = 'async';
    /* The CDN sends access-control-allow-origin:*, so request it with CORS.
       Without this, drawing the sheet TAINTS the canvas and any getImageData
       or toDataURL on it throws for good. Nothing reads pixels today, but a
       tainted canvas is a trap left for whoever adds that later. */
    im.crossOrigin = 'anonymous';
    im.onload = function () {
      if (!im.naturalWidth) return;
      sheetImg = im; sheetReady = true;
      for (var i = 0; i < sheetWaiters.length; i++) { try { sheetWaiters[i](); } catch (e) {} }
      sheetWaiters.length = 0;
    };
    im.onerror = function () { sheetImg = null; };   // keep the drawn knob
    im.src = KNOB_SHEET;
  })();
  var TOUCH = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
  var CEIL  = 0.62;   // peaks are held toward this
  var FLOOR = 0.38;   // quiet parts are lifted toward this
  var N     = 1400;   // samples in the seamless loop

  function smooth(t) { t = Math.max(0, Math.min(1, t)); return t * t * (3 - 2 * t); }

  function envelope(t) {
    if (t < 1.2) return [0, 0];
    if (t < 3.6) return [smooth((t - 1.2) / 2.4), 0];
    if (t < 6.0) return [1, smooth((t - 3.6) / 2.4)];
    if (t < 9.2) return [1, 1];
    var d = 1 - smooth((t - 9.2) / 2.2);
    return [d, d];
  }

  // Deliberately partial. It should read as controlled, not clamped.
  function process(a, builder, body) {
    if (a > CEIL)  a = CEIL + (a - CEIL) * (1 - 0.64 * builder);
    if (a < FLOOR) a = a + (FLOOR - a) * 0.86 * body;
    return a;
  }

  function buildSource() {
    var src = new Float32Array(N), i, k;
    for (i = 0; i < N; i++) src[i] = 0.10 + 0.07 * Math.random();

    // broad passages of activity
    for (k = 0; k < 7; k++) {
      var c0 = Math.random() * N, w0 = N * (0.03 + 0.05 * Math.random()), a0 = 0.18 + 0.22 * Math.random();
      for (i = 0; i < N; i++) {
        var d = Math.abs(i - c0); d = Math.min(d, N - d);
        src[i] += a0 * Math.exp(-(d * d) / (2 * w0 * w0));
      }
    }
    // random peaks
    for (k = 0; k < 34; k++) {
      var c1 = Math.random() * N, w1 = N * (0.004 + 0.011 * Math.random()), a1 = 0.22 + 0.42 * Math.random();
      for (i = 0; i < N; i++) {
        var e = Math.abs(i - c1); e = Math.min(e, N - e);
        src[i] += a1 * Math.exp(-(e * e) / (2 * w1 * w1));
      }
    }
    // per-sample grain, then clamp
    for (i = 0; i < N; i++) {
      src[i] *= 0.72 + 0.28 * Math.random();
      if (src[i] > 1) src[i] = 1;
      if (src[i] < 0.045) src[i] = 0.045;
    }
    return src;
  }

  /* Landscape geometry: the prototype's numbers, unchanged.
     Portrait geometry: same drawing, restacked. BARS is scaled so a bar keeps
     the same width in CSS pixels as it has on desktop (908/190 = 4.78px), and
     type is drawn at 1:1 so 19px really is 19px. */
  function makeLayout(cssW) {
    if (cssW >= 700) {
      /* KR 68 = a 136px knob. Its arc sits at KR+11 = 79, so each knob needs
         158px of vertical room; the text band and canvas height are moved down
         to suit rather than letting the knob run off the bottom. */
      var DW = 1000, KR = 68, RING = KR + 11;
      var nameY = 366, capY = 392, ky = capY + 24 + RING;   // 495
      return {
        mode: 'landscape', DW: DW, DH: ky + RING + 22, scale: cssW / DW,
        PADX: 46, MID: 178, MAXA: 132, BARS: 190,
        BW: (DW - 46 * 2) / 190,
        hintY: 336,           // wave ends 310, names sit at 366
        hintSize: 15,
        knobs: [
          { cx: 360, ky: ky, kr: KR, nameY: nameY, capY: capY },
          { cx: 640, ky: ky, kr: KR, nameY: nameY, capY: capY }
        ]
      };
    }
    /* Portrait stacks the two knobs, so 136px knobs cost 2 x 158px of height.
       The wave band is pulled in (MAXA 94 -> 82) and the gaps tightened so the
       canvas grows as little as possible while staying uncramped. */
    var PADX = 20, inner = cssW - PADX * 2;
    var BARS = Math.max(36, Math.round(190 * inner / 908));
    var KR = 68, RING = KR + 11;
    var MID = 118, MAXA = 82;                 // wave band 36..200
    var n1 = MID + MAXA + 46, c1 = n1 + 26, k1 = c1 + 22 + RING;
    var n2 = k1 + RING + 32,  c2 = n2 + 26, k2 = c2 + 22 + RING;
    return {
      mode: 'portrait', DW: cssW, DH: k2 + RING + 20, scale: 1,
      PADX: PADX, MID: MID, MAXA: MAXA, BARS: BARS,
      BW: inner / BARS,
      hintY: MID + MAXA + 26, // wave ends 200, first name sits at 246
      hintSize: 14,
      knobs: [
        { cx: cssW / 2, ky: k1, kr: KR, nameY: n1, capY: c1 },
        { cx: cssW / 2, ky: k2, kr: KR, nameY: n2, capY: c2 }
      ]
    };
  }

  function setup(root) {
    var canvas = root.querySelector('[data-st-waveform-canvas]');
    if (!canvas || root.__stWfInit) return;
    root.__stWfInit = true;
    var x = canvas.getContext('2d');
    if (!x) return;

    /* Read the store's design tokens so this always matches the page.
       They live on .st (streaky-system.css), not :root, so read them from this
       section's own .st element. Falls back to the literal values. */
    function token(name, fallback) {
      var v = getComputedStyle(root).getPropertyValue(name).trim();
      return v || fallback;
    }
    var FONT   = token('--st-font',   'Inter, system-ui, sans-serif');
    var ACCENT = token('--st-accent', '#0071e3');
    var TEXT   = token('--st-text',   '#f5f5f7');
    var CARD   = token('--st-card',   '#161618');
    var BG2    = token('--st-bg-2',   '#0a0a0b');
    var BG     = token('--st-bg',     '#000');

    var src = buildSource();
    var L = null, dpr = 1;

    /* Interaction state. `val` is the single source of truth once `manual` is
       true, and nothing below ever resets it: that is what carries the knob
       positions through an off-screen pause and back. */
    var manual = false;
    var val = { body: 0, builder: 0 };
    var shown = { body: 0, builder: 0 };   // what the last frame actually drew


    var reduceQ = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    function reduced() { return !!(reduceQ && reduceQ.matches); }

    var holder = root.querySelector('.st-wf__holder');

    /* Returns false and commits nothing if the holder has no width yet: the
       section can be measured before layout settles, or inside a collapsed
       container. Committing a 1px layout there would stick permanently, since
       nothing else recomputes it. The ResizeObserver below picks it up instead. */
    function resize() {
      var measured = holder ? holder.clientWidth : 0;
      if (measured < 2) return false;
      var cssW = Math.min(measured, 1000);
      L = makeLayout(cssW);
      var cssH = L.DH * L.scale;
      dpr = Math.max(1, Math.min(3, window.devicePixelRatio || 1));
      canvas.style.width  = cssW + 'px';
      canvas.style.height = cssH + 'px';
      canvas.width  = Math.round(cssW * dpr);   // backing store, retina sharp
      canvas.height = Math.round(cssH * dpr);
      // assigning width/height resets the context, so set the transform after
      x.setTransform(dpr * L.scale, 0, 0, dpr * L.scale, 0, 0);
      placeProxies();
      return true;
    }

    function dashLine(y, alpha, colour) {
      x.save();
      x.setLineDash([6, 9]);
      x.strokeStyle = colour; x.globalAlpha = alpha; x.lineWidth = 1.5;
      x.beginPath(); x.moveTo(L.PADX, y); x.lineTo(L.DW - L.PADX, y); x.stroke();
      x.restore();
    }

    function knob(g, val, label, caption, active, reverse) {
      var cx = g.cx, KY = g.ky, KR = g.kr;
      var ang = (reverse ? (135 - 270 * val) : (-135 + 270 * val)) * Math.PI / 180;
      x.save();
      // value arc, unchanged
      x.strokeStyle = TEXT; x.globalAlpha = 0.13; x.lineWidth = 4;
      x.beginPath(); x.arc(cx, KY, KR + 11, Math.PI * 0.75, Math.PI * 2.25); x.stroke();
      x.globalAlpha = 0.85; x.strokeStyle = ACCENT;
      x.beginPath();
      if (reverse) x.arc(cx, KY, KR + 11, Math.PI * 2.25 - Math.PI * 1.5 * val, Math.PI * 2.25);
      else         x.arc(cx, KY, KR + 11, Math.PI * 0.75, Math.PI * 0.75 + Math.PI * 1.5 * val);
      x.stroke();
      x.globalAlpha = 1;

      if (sheetReady && sheetImg) {
        /* One sheet drives both knobs: BODY reads it forwards, BUILDER
           backwards, which is what makes BUILDER turn anticlockwise. */
        var fs = sheetImg.naturalWidth / KNOB_COLS;
        var fi = Math.round(val * (KNOB_FRAMES - 1));
        if (reverse) fi = (KNOB_FRAMES - 1) - fi;
        if (fi < 0) fi = 0; else if (fi > KNOB_FRAMES - 1) fi = KNOB_FRAMES - 1;
        var dsz = (KR * 2) / KNOB_FILL;
        x.imageSmoothingEnabled = true;
        x.imageSmoothingQuality = 'high';
        x.drawImage(sheetImg,
          (fi % KNOB_COLS) * fs, Math.floor(fi / KNOB_COLS) * fs, fs, fs,
          cx - dsz / 2, KY - dsz / 2, dsz, dsz);
        x.restore();
        drawKnobText(g, val, label, caption, active);
        return;
      }

      /* ---- fallback: drawn knob, used until the sheet decodes ---- */
      var grd;   // NB: `g` is the layout object param, do not shadow it

      // outer bevel ring
      grd = x.createLinearGradient(cx - KR, KY - KR, cx + KR, KY + KR);
      grd.addColorStop(0,   '#3a3a3e');
      grd.addColorStop(0.45,'#212124');
      grd.addColorStop(1,   '#0d0d0f');
      x.fillStyle = grd; x.beginPath(); x.arc(cx, KY, KR, 0, 7); x.fill();

      // face
      grd = x.createRadialGradient(cx - KR * 0.35, KY - KR * 0.40, KR * 0.12,
                                 cx, KY, KR * 0.92);
      grd.addColorStop(0,   '#2e2e33');
      grd.addColorStop(0.55,'#1b1b1e');
      grd.addColorStop(1,   '#0e0e10');
      x.fillStyle = grd; x.beginPath(); x.arc(cx, KY, KR - 6, 0, 7); x.fill();

      // fixed highlight, upper left, does not turn with the knob
      grd = x.createLinearGradient(cx - KR, KY - KR, cx + KR * 0.4, KY + KR * 0.4);
      grd.addColorStop(0,   'rgba(255,255,255,0.16)');
      grd.addColorStop(0.5, 'rgba(255,255,255,0.02)');
      grd.addColorStop(1,   'rgba(255,255,255,0)');
      x.fillStyle = grd; x.beginPath(); x.arc(cx, KY, KR - 6, 0, 7); x.fill();

      // inner shadow at the rim
      x.strokeStyle = 'rgba(0,0,0,0.55)'; x.lineWidth = 3;
      x.beginPath(); x.arc(cx, KY, KR - 7, 0, 7); x.stroke();

      // pointer, from a quarter of the radius out to near the rim
      x.strokeStyle = ACCENT; x.lineWidth = 3.5; x.lineCap = 'round';
      x.shadowColor = ACCENT; x.shadowBlur = 8;
      x.beginPath();
      x.moveTo(cx + KR * 0.25 * Math.sin(ang), KY - KR * 0.25 * Math.cos(ang));
      x.lineTo(cx + KR * 0.82 * Math.sin(ang), KY - KR * 0.82 * Math.cos(ang));
      x.stroke();
      x.shadowBlur = 0;

      x.restore();
      drawKnobText(g, val, label, caption, active);
    }

    // name and caption, sitting between the waveform and the knob
    function drawKnobText(g, val, label, caption, active) {
      x.save();
      x.textAlign = 'center';
      x.fillStyle = ACCENT; x.globalAlpha = 0.40 + 0.60 * active;
      x.font = '700 19px ' + FONT; x.letterSpacing = '3.4px';
      x.fillText(label, g.cx, g.nameY);
      x.fillStyle = TEXT; x.globalAlpha = 0.20 + 0.55 * active;
      x.font = '400 15px ' + FONT; x.letterSpacing = '0.6px';
      x.fillText(caption, g.cx, g.capY);
      x.letterSpacing = '0px';
      x.restore();
    }

    /* Where the two values come from, in priority order:
         1. the user, the moment they touch a knob, and permanently after that
         2. reduced motion, which shows the treated still frame (both at full)
         3. the demo envelope, for its first LOOPS cycles
         4. zero, once the demo has handed over                                */
    function values(sec) {
      if (manual) return [val.builder, val.body];
      if (reduced()) return [1, 1];
      if (sec < CYCLE * LOOPS) { var e = envelope(sec % CYCLE); return [e[0], e[1]]; }
      return [0, 0];
    }

    function hintAlpha(sec) {
      if (manual) return 0;                       // gone the instant they take over
      if (reduced()) return 1;                    // no fade: a fade is animation
      if (sec < CYCLE * LOOPS) return 0;
      return Math.min(1, (sec - CYCLE * LOOPS) / HINT_FADE);
    }

    function draw(sec) {
      if (!L) return;
      var v = values(sec), builder = v[0], body = v[1];
      // Keep the sliders telling the truth: during the demo and in the
      // reduced-motion frame the knobs move without the user touching them.
      shown.builder = builder; shown.body = body;
      announce('body', body); announce('builder', builder);
      // The waveform never stops scrolling, in demo or manual mode. Reduced
      // motion is the one exception: frozen, but still redrawn on knob changes.
      var head = reduced() ? 0 : sec * 78;

      x.clearRect(0, 0, L.DW, L.DH);
      x.fillStyle = BG; x.fillRect(0, 0, L.DW, L.DH);

      // guide lines
      dashLine(L.MID - CEIL  * L.MAXA, 0.16 + 0.5 * builder, builder > 0.02 ? ACCENT : TEXT);
      dashLine(L.MID + CEIL  * L.MAXA, 0.16 + 0.5 * builder, builder > 0.02 ? ACCENT : TEXT);
      dashLine(L.MID - FLOOR * L.MAXA, 0.16 + 0.5 * body,    body    > 0.02 ? ACCENT : TEXT);
      dashLine(L.MID + FLOOR * L.MAXA, 0.16 + 0.5 * body,    body    > 0.02 ? ACCENT : TEXT);

      // waveform
      for (var i = 0; i < L.BARS; i++) {
        var idx = Math.floor(head + i * 1.9) % N;
        var raw = src[idx];
        var a = process(raw, builder, body) * L.MAXA;
        var bx = L.PADX + i * L.BW;
        var lit = (raw > CEIL && builder > 0.05) || (raw < FLOOR && body > 0.05);
        x.fillStyle = lit ? '#8ec5ff' : TEXT;
        x.globalAlpha = lit ? 0.72 : 0.38;
        x.fillRect(bx, L.MID - a, L.BW * 0.62, a * 2);
      }
      x.globalAlpha = 1;

      knob(L.knobs[0], body,    'BODY',    'lifts the quiet parts', body,    false);
      knob(L.knobs[1], builder, 'BUILDER', 'holds the peaks',       builder, true);

      var ha = hintAlpha(sec);
      if (ha > 0.01) {
        x.save();
        x.textAlign = 'center'; x.fillStyle = TEXT; x.globalAlpha = 0.55 * ha;
        x.letterSpacing = '1.6px';
        var msg = (TOUCH ? 'Tap' : 'Drag') + ' the knobs and hear it for yourself';
        var size = L.hintSize;
        x.font = '400 ' + size + 'px ' + FONT;
        // shrink rather than overflow on a narrow canvas
        var room = L.DW - L.PADX * 2;
        while (size > 10 && x.measureText(msg).width > room) {
          size -= 1; x.font = '400 ' + size + 'px ' + FONT;
        }
        x.fillText(msg, L.DW / 2, L.hintY);
        x.letterSpacing = '0px';
        x.restore();
      }
    }

    // ---- interaction ------------------------------------------------------
    /* Hit testing reads L.knobs every time. There are two layouts with the
       knobs in different places, and the ResizeObserver can swap between them
       at any moment, so caching a centre would silently miss. L is rebuilt by
       resize(), which means the hit test is always against what is on screen. */
    function toDesign(ev) {
      var r = canvas.getBoundingClientRect();
      if (!r.width || !r.height || !L) return null;
      return { x: (ev.clientX - r.left) * (L.DW / r.width),
               y: (ev.clientY - r.top)  * (L.DH / r.height) };
    }
    function hitKnob(p) {
      if (!p || !L) return null;
      for (var i = 0; i < L.knobs.length; i++) {
        var g = L.knobs[i], dx = p.x - g.cx, dy = p.y - g.ky, rr = g.kr + 16;
        if (dx * dx + dy * dy <= rr * rr) return KEYS[i];
      }
      return null;
    }

    /* Seed from what is currently on screen, otherwise grabbing a knob that the
       demo has driven to 0.7 would snap it to whatever val happened to hold. */
    function takeOver() {
      if (!manual) { val.body = shown.body; val.builder = shown.builder; manual = true; }
    }

    function setVal(k, v) {
      val[k] = Math.max(0, Math.min(1, v));
      syncProxy(k);
      // While the loop runs it repaints anyway. Paused or reduced-motion, the
      // knob still has to answer immediately.
      if (!running) draw(elapsed / 1000);
    }

    var drag = null;

    canvas.addEventListener('pointerdown', function (ev) {
      var k = hitKnob(toDesign(ev));
      if (!k) return;
      takeOver();
      if (prox[k]) { try { prox[k].focus({ preventScroll: true }); } catch (e) { prox[k].focus(); } }

      if (ev.pointerType === 'touch') {
        /* Tap to step: off, half, full, off. Deliberately not a drag. A vertical
           drag inside a tall canvas would claim the thumb gesture the page needs
           for scrolling. No preventDefault here, so scrolling stays the user's. */
        setVal(k, val[k] >= 0.99 ? 0 : (val[k] < 0.49 ? 0.5 : 1));
        return;
      }
      ev.preventDefault();
      drag = { k: k, y: ev.clientY, start: val[k] };
      try { canvas.setPointerCapture(ev.pointerId); } catch (e) {}
      canvas.style.cursor = 'grabbing';
    });

    canvas.addEventListener('pointermove', function (ev) {
      if (!drag) {
        if (ev.pointerType !== 'touch') {
          canvas.style.cursor = hitKnob(toDesign(ev)) ? 'grab' : '';
        }
        return;
      }
      // CSS pixels, so the physical drag distance feels the same at any width.
      setVal(drag.k, drag.start + (DRAG_SIGN[drag.k] || 1) * (drag.y - ev.clientY) / DRAG_FULL);
    });

    function endDrag(ev) {
      if (!drag) return;
      if (ev && ev.pointerId != null) { try { canvas.releasePointerCapture(ev.pointerId); } catch (e) {} }
      drag = null;
      canvas.style.cursor = '';
    }
    canvas.addEventListener('pointerup', endDrag);
    canvas.addEventListener('pointercancel', endDrag);

    // ---- keyboard / screen reader proxies ----------------------------------
    /* One role="slider" per knob, positioned over it and kept in sync with the
       live layout. pointer-events:none so all pointer input still goes to the
       canvas; these exist for focus, keys and announcement. */
    var prox = {};
    var LABELS = { body: 'BODY', builder: 'BUILDER' };

    function announce(k, v) {
      var el = prox[k]; if (!el) return;
      var pc = Math.round(v * 100);
      if (el.__pc === pc) return;          // no DOM write unless it changed
      el.__pc = pc;
      el.setAttribute('aria-valuenow', pc);
      el.setAttribute('aria-valuetext', pc + '%');
    }
    function syncProxy(k) { announce(k, val[k]); }

    function placeProxies() {
      if (!L) return;
      var ox = canvas.offsetLeft, oy = canvas.offsetTop, sc = L.scale;
      for (var i = 0; i < KEYS.length; i++) {
        var k = KEYS[i], g = L.knobs[i], el = prox[k];
        if (!el) continue;
        var d = 2 * (g.kr + 16) * sc;
        el.style.width = el.style.height = Math.round(d) + 'px';
        el.style.left = Math.round(ox + g.cx * sc - d / 2) + 'px';
        el.style.top  = Math.round(oy + g.ky * sc - d / 2) + 'px';
      }
    }

    (function buildProxies() {
      for (var i = 0; i < KEYS.length; i++) {
        var k = KEYS[i];
        var el = document.createElement('div');
        el.className = 'st-wf__knobctl';
        el.setAttribute('role', 'slider');
        el.setAttribute('tabindex', '0');
        el.setAttribute('aria-label', LABELS[k]);
        el.setAttribute('aria-valuemin', '0');
        el.setAttribute('aria-valuemax', '100');
        el.setAttribute('aria-orientation', 'vertical');
        prox[k] = el;
        syncProxy(k);
        holder.appendChild(el);
      }
    })();

    function onKey(ev) {
      var k = null;
      for (var i = 0; i < KEYS.length; i++) if (prox[KEYS[i]] === ev.target) k = KEYS[i];
      if (!k) return;
      /* Arrow direction follows the drag, so BUILDER rises on Down. Home/End
         stay absolute: they are value extremes, not directions. */
      var sgn = DRAG_SIGN[k] || 1;
      var step = 0.05 * sgn, v = val[k], handled = true;
      switch (ev.key) {
        case 'ArrowUp': case 'ArrowRight': v += step; break;
        case 'ArrowDown': case 'ArrowLeft': v -= step; break;
        case 'PageUp':   v += step * 4; break;
        case 'PageDown': v -= step * 4; break;
        case 'Home': v = 0; break;
        case 'End':  v = 1; break;
        default: handled = false;
      }
      if (!handled) return;
      ev.preventDefault();
      takeOver();
      setVal(k, v);
    }
    holder.addEventListener('keydown', onKey);

    // ---- run loop: paused whenever the section is off screen ---------------
    var running = false, rafId = null, t0 = null, banked = 0, elapsed = 0;

    function loop(now) {
      if (!running) return;
      if (!L && !resize()) { rafId = requestAnimationFrame(loop); return; }
      if (t0 === null) t0 = now;
      elapsed = banked + (now - t0);
      draw(elapsed / 1000);
      rafId = requestAnimationFrame(loop);
    }
    function play() {
      if (running || reduced()) return;
      running = true; t0 = null;
      rafId = requestAnimationFrame(loop);
    }
    function pause() {
      if (!running) return;
      running = false;
      if (rafId) cancelAnimationFrame(rafId);
      rafId = null;
      banked = elapsed;               // resume exactly where it stopped
      t0 = null;
    }
    /* Always paints. resize() reassigns canvas.width, which CLEARS the canvas,
       so skipping the draw here because the loop "is running" can leave it blank
       until the next rAF, and in a hidden or throttled tab that frame may never
       arrive. Painting one extra frame costs nothing. */
    function repaint() {
      if (!L && !resize()) return;
      if (reduced()) pause();
      draw(elapsed / 1000);
    }

    resize();
    repaint();
    // swap the vector knob for the artwork the moment the sheet decodes
    if (!sheetReady) sheetWaiters.push(function () { repaint(); });

    var onPref = null;
    if (reduceQ) {
      onPref = function () { if (reduced()) { pause(); draw(elapsed / 1000); } else play(); };
      if (reduceQ.addEventListener) reduceQ.addEventListener('change', onPref);
      else if (reduceQ.addListener) reduceQ.addListener(onPref);
    }

    var inView = false;
    var io = null;
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { inView = e.isIntersecting; inView ? play() : pause(); });
      }, { rootMargin: '120px' });
      io.observe(root);
    } else {
      inView = true;
      play();
    }

    var rz = null;
    var relayout = function () {
      if (rz) cancelAnimationFrame(rz);
      // No rAF in a hidden document, so debouncing through it would never land.
      if (document.hidden) { if (resize()) repaint(); return; }
      rz = requestAnimationFrame(function () { if (resize()) repaint(); });
    };
    window.addEventListener('resize', relayout);

    var ro = null;
    if ('ResizeObserver' in window && holder) {
      ro = new ResizeObserver(relayout);
      ro.observe(holder);
    }

    /* A hidden tab fires no rAF, so pause and bank the time. Coming back needs
       an explicit resume: the IntersectionObserver will not fire again if the
       intersection itself never changed, so without this the section stays dead
       after a tab switch. */
    var onVis = function () {
      if (document.hidden) { pause(); return; }
      relayout();                 // a size change while hidden never got applied
      if (inView) play();
    };
    document.addEventListener('visibilitychange', onVis);

    /* Theme editor: the section's DOM is replaced on every edit. Without this the
       old instance keeps its rAF loop and listeners alive against a detached
       canvas. Only matters in the editor, but it is cheap to do properly. */
    root.__stWfDestroy = function () {
      pause();
      if (io) io.disconnect();
      if (ro) ro.disconnect();
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('resize', relayout);
      holder.removeEventListener('keydown', onKey);
      for (var i = 0; i < KEYS.length; i++) { var el = prox[KEYS[i]]; if (el && el.parentNode) el.parentNode.removeChild(el); }
      if (reduceQ && onPref) {
        if (reduceQ.removeEventListener) reduceQ.removeEventListener('change', onPref);
        else if (reduceQ.removeListener) reduceQ.removeListener(onPref);
      }
    };


    /* Canvas does not wait for webfonts, so the first frames would render in the
       fallback face. Repaint once the real face is ready. */
    if (document.fonts && document.fonts.load) {
      document.fonts.load('700 19px ' + FONT).then(repaint).catch(function () {});
    }
  }

  function init() {
    var roots = document.querySelectorAll('[data-st-waveform]');
    for (var i = 0; i < roots.length; i++) setup(roots[i]);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Theme editor: re-init when the section is re-rendered, tear down when removed.
  document.addEventListener('shopify:section:load', function () { init(); });
  document.addEventListener('shopify:section:unload', function (e) {
    var scope = (e && e.target && e.target.querySelectorAll) ? e.target : document;
    var roots = scope.querySelectorAll('[data-st-waveform]');
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].__stWfDestroy) roots[i].__stWfDestroy();
    }
  });
})();
